import { Button, Card, Flex, Form, Input, Typography } from "antd";
import React, { useMemo, useState } from "react";
import AuthService from "../../Services/auth.service";
import { useNavigate, useSearchParams } from "react-router-dom";
import FailureResult from "../../Components/Atoms/FailureResult/FailureResult";
import SuccessResult from "../../Components/Atoms/SuccessResult/SuccessResult";
import { useLoader } from "../../Context/LoaderContext/LoaderContext";
import { getPageMetaData } from "../../Utils/meta-data";
import { useAppContext } from "../../Context/AppContext/AppContext";
import PageMetadata from "../../Components/PageMetaData/PageMetaData";

const failureReasons = [
  "The password reset link has expired or is invalid.",
  "The reset process cannot be completed due to server or email service issues.",
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ResetPassword = () => {
  const navigate = useNavigate();
  const { setLoading, setLoadingMessage, isLoading } = useLoader();
  const [showFailure, setShowFailure] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const { organization } = useAppContext();

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const redirectUrlRaw = searchParams.get("redirect_url");
  const redirectUrl = useMemo(() => {
    if (!redirectUrlRaw) return null;
    try {
      return decodeURIComponent(redirectUrlRaw);
    } catch {
      return redirectUrlRaw;
    }
  }, [redirectUrlRaw]);
  const emailParam = searchParams.get("email");
  const flow = searchParams.get("flow");

  const email = useMemo(() => {
    if (!emailParam) return null;
    try {
      return decodeURIComponent(emailParam);
    } catch {
      return emailParam;
    }
  }, [emailParam]);

  const isAccountSetup = flow === "account-setup";
  const emailValid = email === null || EMAIL_REGEX.test(email);
  const prefilledEmail = email && emailValid ? email : null;

  const authService = AuthService.getInstance();

  const handlePasswordSubmit = async (values: any) => {
    if (!token) return;
    setLoading(true);
    setShowFailure(false);
    try {
      const password = values.password;
      const response = await authService.resetPassword(token, password);
      if (response?.status !== 201) {
        setShowFailure(true);
        return;
      }
      const hasEmailAndRedirect =
        prefilledEmail &&
        redirectUrl &&
        redirectUrl.startsWith("/") &&
        !redirectUrl.startsWith("//");
      if (hasEmailAndRedirect) {
        setLoadingMessage("Signing you in...");
        const signInResponse = await authService.signIn({
          email: prefilledEmail,
          password: values.password,
        });
        setLoadingMessage(null);
        const signInOk =
          signInResponse?.status != null &&
          signInResponse.status >= 200 &&
          signInResponse.status < 300;
        if (signInOk) {
          setLoading(false);
          setLoadingMessage(null);
          navigate(redirectUrl);
          return;
        }
      }
      setShowSuccess(true);
    } catch {
      setShowFailure(true);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleGoToSignIn = () => {
    if (redirectUrl) {
      navigate(`/sign-in?redirect_url=${encodeURIComponent(redirectUrl)}`);
    } else {
      navigate("/sign-in");
    }
  };

  const handleTryAgain = () => {
    setShowFailure(false);
    setShowSuccess(false);
    navigate("/forgot-password");
  };

  const handleInvalidLinkRequestNew = () => {
    navigate("/forgot-password");
  };

  const meta = organization
    ? getPageMetaData("resetpassword", { organization })
    : getPageMetaData("resetpassword");

  // Missing token: show error state, do not allow submit
  if (!token && !showSuccess && !showFailure) {
    return (
      <>
        <PageMetadata title={meta.title} description={meta.description} />
        <Flex className="signin" align="start" justify="center">
          <Card
            className="card"
            bordered
            style={{ maxWidth: 360, width: "100%" }}
          >
            <Typography.Title level={4} style={{ margin: 0, marginBottom: 8 }}>
              Invalid or expired link
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
              This link is invalid or has expired. Request a new reset link to
              set or reset your password.
            </Typography.Paragraph>
            <Button type="primary" block onClick={handleInvalidLinkRequestNew}>
              Request a new reset link
            </Button>
          </Card>
        </Flex>
      </>
    );
  }

  return (
    <>
      <PageMetadata title={meta.title} description={meta.description} />
      <Flex className="signin" align="center" justify="center">
        {!showFailure && !showSuccess && token && (
          <Card
            className="card"
            bordered
            style={{ maxWidth: 360, width: "100%" }}
            title={
              <Typography.Title level={4} style={{ margin: 0 }}>
                {isAccountSetup
                  ? "Complete your account setup"
                  : "Reset your password"}
              </Typography.Title>
            }
          >
            {isAccountSetup ? (
              <Typography.Paragraph
                type="secondary"
                style={{ marginTop: 8, marginBottom: 16 }}
              >
                Your profile was shortlisted by{" "}
                {organization?.orgName || "the company"}. You just need to set up
                your password and you can start with the assessment.
              </Typography.Paragraph>
            ) : prefilledEmail ? (
              <Typography.Paragraph
                type="secondary"
                style={{ marginTop: 8, marginBottom: 16 }}
              >
                You're resetting the password for{" "}
              </Typography.Paragraph>
            ) : null}

            

            <Form
              name="password_reset_form"
              className="login-form"
              initialValues={{
                remember: true,
                ...(prefilledEmail ? { email: prefilledEmail } : {}),
              }}
              onFinish={handlePasswordSubmit}
              autoComplete="off"
            >
              {prefilledEmail && (
                <Form.Item name="email" >
                  <Input
                    readOnly
                    disabled
                    style={{ cursor: "not-allowed", color: "inherit" }}
                  />
                </Form.Item>
              )}

              {email && !emailValid && (
                <Typography.Text type="warning" style={{ display: "block", marginBottom: 8 }}>
                  The email provided in the link appears to be invalid. Please contact support.
                </Typography.Text>
              )}

              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Please enter your new password!",
                  },
                  {
                    min: 8,
                    message: "Password must be at least 8 characters long!",
                  },
                  {
                    pattern: /[A-Z]/,
                    message:
                      "Password must contain at least one uppercase letter!",
                  },
                  {
                    pattern: /[a-z]/,
                    message:
                      "Password must contain at least one lowercase letter!",
                  },
                  {
                    pattern: /[0-9]/,
                    message:
                      "Password must contain at least one number!",
                  },
                  {
                    pattern: /[!@#$%^&*(),.?":{}|<>]/,
                    message:
                      "Password must contain at least one special character!",
                  },
                ]}
              >
                <Input.Password placeholder="New Password" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                rules={[
                  {
                    required: true,
                    message: "Please confirm your password!",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match!")
                      );
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm Password" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={isLoading}
                >
                  {isAccountSetup
                    ? "Set password & start assessment"
                    : redirectUrl
                    ? "Create new password"
                    : "Reset password"}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        )}

        {showSuccess && !showFailure && (
          <SuccessResult
            title={isAccountSetup ? "Account setup complete" : "Password changed"}
            subTitle={
              isAccountSetup
                ? "Your password is set. Sign in to access your account and start your assessment."
                : "Your password has been successfully reset. Please go to the login page to access your account."
            }
            onClick={handleGoToSignIn}
            buttonTitle={isAccountSetup ? "Sign in & start assessment" : "Go to login"}
          />
        )}

        {showFailure && !showSuccess && (
          <FailureResult
            title="Reset Password Failed"
            subTitle=""
            onClick={handleTryAgain}
            reason={failureReasons}
          />
        )}
      </Flex>
    </>
  );
};

export default ResetPassword;
